<template>
  <div
    :style="style"
    :class="containerKls"
    data-component-name="Steps"
    data-event-name="change"
    data-event-content="步骤切换"
  >
    <div
      v-if="!isSimple"
      :class="[ns.e('line'), ns.is(currentStatus)]"
      :style="lineWrapStyle"
    >
      <i :class="ns.e('line-inner')" :style="lineStyle" />
    </div>
    <!-- icon & line -->
    <div :class="[ns.e('head'), ns.is(currentStatus)]">
      <div
        :class="[ns.e('icon'), ns.is(icon || $slots.icon ? 'icon' : 'text')]"
        :style="iconStyle"
      >
        <slot v-if="currentStatus !== 'success'" name="icon">
          <jd-icon v-if="icon" :class="ns.e('icon-inner')">
            <component :is="icon" />
          </jd-icon>
          <div v-if="!icon && !isSimple" :class="ns.e('icon-inner')">
            {{ index + 1 }}
          </div>
        </slot>
        <slot v-else-if="currentStatus === 'success'" name="success-icon">
          <jd-icon :class="[ns.e('icon-inner'), ns.is('status')]">
            <component :is="currentSuccessIcon" />
          </jd-icon>
        </slot>
      </div>
    </div>
    <!-- title & description -->
    <div :class="ns.e('main')" :style="mainStyle">
      <div :class="[ns.e('title'), ns.is(currentStatus)]" :style="titleStyle">
        <slot name="title">{{ title }}</slot>
      </div>
      <div v-if="isSimple" :class="ns.e('arrow')" />
      <div
        v-else
        :class="[ns.e('description'), ns.is(currentStatus)]"
        :style="descriptionStyle"
      >
        <slot name="description">{{ description }}</slot>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  computed,
  getCurrentInstance,
  inject,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue'
import { Check } from '@jd/jdesign-icons-vue'
import { useNamespace } from '@jdesign-vue/hooks'
import { JdIcon } from '@jdesign-vue/components/icon'
import { isNumber } from '@jdesign-vue/utils'
import { stepProps } from './item'

import type { CSSProperties, Ref } from 'vue'

export interface IStepsProps {
  size: string
  fillColor: string
  space: number | string
  active: number
  direction: string
  alignCenter: boolean
  simple: boolean
  finishStatus: string
  processStatus: string
  headSpace: number
  titleSpace: number
  descriptionSpace: number
}

export interface StepItemState {
  uid: number
  currentStatus: string
  setIndex: (val: number) => void
  calcProgress: (status: string) => void
}

export interface IStepsInject {
  props: IStepsProps
  steps: Ref<StepItemState[]>
  addStep: (item: StepItemState) => void
  removeStep: (uid: number) => void
}

defineOptions({
  name: 'JdStep',
})

const props = defineProps(stepProps)
const ns = useNamespace('step')
const index = ref(-1)
const lineStyle = ref({})
const internalStatus = ref('')
const parent = inject('JdSteps') as IStepsInject
const currentInstance = getCurrentInstance()
// 计算属性：处理成功图标的默认值
const currentSuccessIcon = computed(() => {
  return props.successIcon || Check
})
onMounted(() => {
  watch(
    [
      () => parent.props.active,
      () => parent.props.processStatus,
      () => parent.props.finishStatus,
    ],
    ([active]) => {
      updateStatus(active)
    },
    { immediate: true }
  )
})

onBeforeUnmount(() => {
  parent.removeStep(stepItemState.uid)
})

const currentStatus = computed(() => {
  return props.status || internalStatus.value
})

const prevStatus = computed(() => {
  const prevStep = parent.steps.value[index.value - 1]
  return prevStep ? prevStep.currentStatus : 'wait'
})

const isCenter = computed(() => {
  return parent.props.alignCenter
})

const isVertical = computed(() => {
  return parent.props.direction === 'vertical'
})

const isSimple = computed(() => {
  return parent.props.simple
})

const stepsCount = computed(() => {
  return parent.steps.value.length
})

const isLast = computed(() => {
  return parent.steps.value[stepsCount.value - 1]?.uid === currentInstance?.uid
})

const space = computed(() => {
  return isSimple.value ? '' : parent.props.space
})

const containerKls = computed(() => {
  return [
    ns.b(),
    ns.m(parent.props.size),
    ns.is(isSimple.value ? 'simple' : parent.props.direction),
    ns.is('flex', isLast.value && !space.value && !isCenter.value),
    ns.is('center', isCenter.value && !isVertical.value && !isSimple.value),
  ]
})

const iconStyle = computed(() => {
  if (parent.props.size === 'large') {
    return {
      width: `${28 + parent.props.headSpace * 2}px`,
      height: `${28 + parent.props.headSpace * 2}px`,
      borderWidth: `${parent.props.headSpace}px`,
      borderColor: `${parent.props.fillColor}`,
      backgroundColor: ['success', 'finish'].includes(currentStatus.value)
        ? `var(${ns.cssVarName('color-step-info-background')})`
        : ['wait'].includes(currentStatus.value)
          ? `${parent.props.fillColor}`
          : '',
    }
  } else {
    return {
      width: `${22 + parent.props.headSpace * 2}px`,
      height: `${22 + parent.props.headSpace * 2}px`,
      borderWidth: `${parent.props.headSpace}px`,
      borderColor: `${parent.props.fillColor}`,
      backgroundColor: ['success', 'finish'].includes(currentStatus.value)
        ? `var(${ns.cssVarName('color-step-info-background')})`
        : ['wait'].includes(currentStatus.value)
          ? `${parent.props.fillColor}`
          : '',
    }
  }
})

const mainStyle = computed(() => {
  if (parent.props.direction === 'vertical') {
    return {
      paddingLeft: `${parent.props.headSpace}px`,
    }
  }
  return {}
})
const titleStyle = computed(() => {
  if (parent.props.direction === 'horizontal') {
    return parent.props.alignCenter
      ? {
          marginTop: `${parent.props.titleSpace - parent.props.headSpace}px`,
          backgroundColor: `${parent.props.fillColor}`,
        }
      : {
          marginTop: `${
            parent.props.size === 'large'
              ? parent.props.headSpace + 2
              : parent.props.headSpace
          }px`,
          backgroundColor: `${parent.props.fillColor}`,
        }
  } else {
    return {
      backgroundColor: `${parent.props.fillColor}`,
      marginTop: `${parent.props.headSpace - 1}px`,
    }
  }
})

const descriptionStyle = computed(() => {
  return { marginTop: `${parent.props.descriptionSpace}px` }
})

const lineWrapStyle = computed(() => {
  if (parent.props.size === 'large') {
    return { top: `${(28 + parent.props.headSpace * 2) / 2}px` }
  } else {
    return { top: `${(22 + parent.props.headSpace * 2) / 2}px` }
  }
})

const style = computed(() => {
  const style: CSSProperties = {
    flexBasis: isNumber(space.value)
      ? `${space.value}px`
      : space.value
      ? space.value
      : `${100 / (stepsCount.value - (isCenter.value ? 0 : 1))}%`,
  }
  if (isVertical.value) return style
  if (isLast.value) {
    style.maxWidth = `${100 / stepsCount.value}%`
  }
  return style
})

const setIndex = (val: number) => {
  index.value = val
}

const calcProgress = (status: string) => {
  const isWait = status === 'wait'
  const style: CSSProperties = {
    transitionDelay: `${isWait ? '-' : ''}${150 * index.value}ms`,
  }
  const step = status === parent.props.processStatus || isWait ? 0 : 100

  style.borderWidth = step && !isSimple.value ? '1px' : 0
  style[parent.props.direction === 'vertical' ? 'height' : 'width'] = `${step}%`
  lineStyle.value = style
}

const updateStatus = (activeIndex: number) => {
  if (activeIndex > index.value) {
    internalStatus.value = parent.props.finishStatus
  } else if (activeIndex === index.value && prevStatus.value !== 'error') {
    internalStatus.value = parent.props.processStatus
  } else {
    internalStatus.value = 'wait'
  }
  const prevChild = parent.steps.value[stepsCount.value - 1]
  if (prevChild) prevChild.calcProgress(internalStatus.value)
}

const stepItemState = reactive({
  uid: currentInstance!.uid,
  currentStatus,
  setIndex,
  calcProgress,
})

parent.addStep(stepItemState)
</script>
